import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { freelancingProjects } from '../../data/constants';

// Freelancing Container
const FreelancingContainer = styled.div`
  width: 100%;
  padding: 60px 0 80px 0;
  display: flex;
  justify-content: center;
  background: transparent;
  position: relative;
  scroll-margin-top: 100px;
  
  @media (max-width: 960px) {
    padding: 40px 0 60px 0;
  }
`;

// Freelancing Wrapper
const FreelancingWrapper = styled.div`
  width: 100%;
  max-width: 1350px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
`;

// Title of the Section
const Title = styled.h1`
  font-family: 'Mosca Laroke', 'Playfair Display', 'Bodoni Moda', 'Cinzel', serif;
  font-size: 76px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  line-height: 1;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 32px;
  letter-spacing: 0.5px;

  @media (max-width: 1400px) { font-size: 64px; }
  @media (max-width: 1200px) { font-size: 52px; }
  @media (max-width: 768px)  { font-size: 44px; }
`;

// Grid for Freelancing Projects
const FreelancingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  width: 100%;
  max-width: 1200px;
  margin-top: 20px;
  
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

// Individual Freelancing Project Card
const FreelancingCard = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.primary + '40'};
  box-shadow: rgba(133, 76, 230, 0.18) 0px 8px 32px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-8px);
    border-color: ${({ theme }) => theme.primary};
    box-shadow: rgba(133, 76, 230, 0.45) 0px 16px 48px;
  }
  
  @media (max-width: 768px) {
    height: 350px;
  }
`;

// Card Image
const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.85) contrast(1.05);
  transition: transform 0.4s ease-in-out, filter 0.4s ease-in-out;
  
  ${FreelancingCard}:hover & {
    transform: scale(1.06);
    filter: brightness(0.95) contrast(1.1);
  }
`;

// Permanent Dark Purple Color Harmonization Tint
const ImageTintOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(28, 14, 53, 0.45) 0%,
    rgba(133, 76, 230, 0.2) 50%,
    rgba(15, 8, 30, 0.85) 100%
  );
  mix-blend-mode: multiply;
  z-index: 1;
  transition: opacity 0.4s ease;

  ${FreelancingCard}:hover & {
    opacity: 0.5;
  }
`;

// Default Title Banner (visible when idle)
const DefaultTitleBanner = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px 24px;
  background: linear-gradient(
    to top,
    rgba(15, 8, 30, 0.95) 0%,
    rgba(15, 8, 30, 0.6) 70%,
    transparent 100%
  );
  z-index: 2;
  transition: opacity 0.3s ease-in-out;

  ${FreelancingCard}:hover & {
    opacity: 0;
  }
`;

const DefaultTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);

  @media (max-width: 768px) {
    font-size: 19px;
  }
`;

const DefaultSubtitle = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
`;

// Overlay that appears on hover
const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(19, 11, 36, 0.55) 0%,
    rgba(133, 76, 230, 0.25) 40%,
    rgba(15, 8, 30, 0.95) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 24px;
  opacity: 0;
  z-index: 2;
  transition: opacity 0.35s ease-in-out;
  
  ${FreelancingCard}:hover & {
    opacity: 1;
  }
`;

const CardTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  margin-bottom: 12px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const TechBadge = styled.span`
  background: rgba(133, 76, 230, 0.35);
  color: #E2D4FB;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid rgba(133, 76, 230, 0.5);
  backdrop-filter: blur(4px);
`;

const ClientInfo = styled.div`
  font-size: 13px;
  color: #B388FF;
  font-weight: 600;
  margin-top: 4px;
`;

// Category Badge (always visible)
const CategoryBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: linear-gradient(135deg, rgba(133, 76, 230, 0.95), rgba(157, 92, 232, 0.9));
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  z-index: 3;
  box-shadow: 0 4px 15px rgba(133, 76, 230, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
`;

function FreelancingSection() {
  return (
    <FreelancingContainer id="freelancing">
      <FreelancingWrapper
        as={motion.div}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <Title>FREELANCING</Title>
        <FreelancingGrid>
          {freelancingProjects.map((project) => (
            <FreelancingCard key={project.id}>
              <CategoryBadge>{project.category}</CategoryBadge>
              <CardImage src={project.image} alt={project.title} />
              <ImageTintOverlay />
              
              <DefaultTitleBanner>
                <DefaultTitle>{project.title}</DefaultTitle>
                <DefaultSubtitle>✓ {project.clientsServed} Client{project.clientsServed > 1 ? 's' : ''} Served</DefaultSubtitle>
              </DefaultTitleBanner>

              <CardOverlay>
                <CardTitle>{project.title}</CardTitle>
                <CardDescription>{project.description}</CardDescription>
                <TechStack>
                  {project.technologies.slice(0, 4).map((tech, index) => (
                    <TechBadge key={index}>{tech}</TechBadge>
                  ))}
                </TechStack>
                <ClientInfo>
                  ✓ {project.clientsServed} Client{project.clientsServed > 1 ? 's' : ''} Served
                </ClientInfo>
              </CardOverlay>
            </FreelancingCard>
          ))}
        </FreelancingGrid>
      </FreelancingWrapper>
    </FreelancingContainer>
  );
}

export default FreelancingSection;
